import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/layout/Layout";
import Seo from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { subscriptionService } from "@/api/services";
import { toast } from "sonner";
import { Crown, CheckCircle } from "@phosphor-icons/react";

const VIP_CARD =
    "https://images.unsplash.com/photo-1574629810360-7efbbe195018?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80&ixlib=rb-4.0.3";

export default function Subscription() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    if (!user) {
        navigate("/login");
        return null;
    }

    if (user.userLevel === "vip") {
        return (
            <Layout>
                <Seo title="Subscription" path="/subscription" noIndex />
                <div className="max-w-2xl mx-auto px-4 py-24 text-center">
                    <Crown size={48} weight="duotone" className="text-primary mx-auto mb-4" />
                    <h1 className="text-4xl font-black uppercase tracking-tighter mb-3">
                        You're VIP
                    </h1>
                    <p className="text-zinc-400 mb-6">
                        Full access to every tier, every day. Enjoy.
                    </p>
                    <Button
                        onClick={() => navigate("/dashboard")}
                        className="rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider"
                    >
                        Back to Dashboard
                    </Button>
                </div>
            </Layout>
        );
    }

    const onSubscribe = async () => {
        setLoading(true);
        try {
            const callbackUrl = `${window.location.origin}/subscription/callback`;
            const res = await subscriptionService.initialize(callbackUrl);
            if (res?.authorizationUrl) {
                window.location.href = res.authorizationUrl;
            } else {
                throw new Error("Could not start payment");
            }
        } catch (err) {
            toast.error(err?.message || "Payment initialization failed");
            setLoading(false);
        }
    };

    return (
        <Layout>
            <Seo title="Subscription" path="/subscription" noIndex />
            <div className="max-w-5xl mx-auto px-4 md:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                <div className="md:col-span-7">
                    <div className="font-mono text-xs uppercase tracking-[0.4em] text-primary mb-3">
                        ▌ VIP MEMBERSHIP
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-6">
                        Unlock the full
                        <br />
                        <span className="text-primary neon-text">KickLoom edge</span>
                    </h1>
                    <ul className="space-y-3 mb-8">
                        {[
                            "10-game accumulator slip daily",
                            "5-game and 2-game slips included",
                            "Quick custom AI predictions",
                            "Live odds reconciled with bookmakers",
                            "Priority support",
                        ].map((f) => (
                            <li key={f} className="flex items-center gap-3 text-zinc-200">
                                <CheckCircle size={20} weight="fill" className="text-primary flex-shrink-0" />
                                <span>{f}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="bg-card border border-border p-6 flex items-baseline justify-between mb-6">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                                Monthly
                            </div>
                            <div className="font-mono text-4xl font-bold text-white">
                                ₦1,000
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                                Paid via
                            </div>
                            <div className="text-sm font-bold text-primary">Paystack</div>
                        </div>
                    </div>
                    <Button
                        onClick={onSubscribe}
                        disabled={loading}
                        size="lg"
                        className="rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider w-full md:w-auto px-8 h-12"
                        data-testid="subscribe-btn"
                    >
                        {loading ? "Redirecting..." : "Subscribe Now"}
                    </Button>
                    <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-3">
                        Secure payment · Cancel anytime
                    </p>
                </div>
                <div className="md:col-span-5 flex justify-center">
                    <img
                        src={VIP_CARD}
                        alt="VIP membership"
                        className="max-w-full"
                        style={{ filter: "drop-shadow(0 0 30px rgba(0,255,157,0.3))" }}
                    />
                </div>
            </div>
        </Layout>
    );
}
